import React, { useEffect, useMemo, useState } from 'react';
import './VisibilityReportDialog.scss';
import { useTranslation } from 'react-i18next';
import {
  formatPeriodLabel,
  periodLocaleFor,
} from '../../../shared/helpers/filters/formatPeriodLabel';
import { Dialog } from 'primereact/dialog';
import { differenceInCalendarDays, format } from 'date-fns';
import type {
  VisibilityReportModel,
  VisibilityReportPeriodType,
} from '@brand24-original/llm-types';
import { Dropdown } from 'primereact/dropdown';
import DateRangePicker from '../../../shared/components/date-range-picker/DateRangePicker';
import FilterBox from '../../../shared/components/filter-box/FilterBox';
import CheckboxSelect, {
  CheckboxSelectOption,
} from '../../../shared/components/checkbox-select/CheckboxSelect';
import ModelSelect, { useModelSelectOptions } from '../../../shared/components/model-select/ModelSelect';
import {
  calculateDefaultDateRange,
  calculateStandardRange,
  standardPeriodOptions,
} from '../../../shared/helpers/filters/DateRangeHelper';
import { useGetProjectModelDataDateRanges } from '../../../shared/api/hooks/project/stats/useGetProjectModelDataDateRanges';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { usePromptGroups } from '../../../shared/hooks/prompt/UsePromptGroups';
import { StandardPeriodType } from '../../../shared/types/filters/StandardPeriodType';
import { getPeriod } from '../../../shared/helpers/StatsHelpers';
import { ChatType } from '../../../shared/types/monitor/ChatType';
import VisibilityReportPreview from './VisibilityReportPreview';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { chatTypesToReportModels, chatTypesWithData } from '../helpers/reportModels';

/**
 * Ten sam limit, co po stronie API (`VISIBILITY_REPORT_MAX_PERIOD_DAYS`). Produktowo to „3 miesiące";
 * liczba jest wyższa niż 92, bo preset „ostatnie 3 miesiące" daje włącznie do 93 dni.
 */
const MAX_DAYS = 95;

interface VisibilityReportDialogProps {
  visible: boolean;
  onHide: () => void;
  onGenerate: (
    from: string,
    to: string,
    models: VisibilityReportModel[],
    promptIds: number[],
    periodType: VisibilityReportPeriodType
  ) => void;
  isBusy: boolean;
  error?: string | null;
  projectId: number;
  brand: string;
  /** Pierwszy/ostatni dzień z danymi projektu — poza ten zakres nie ma czego raportować. */
  minDate?: Date;
  maxDate?: Date;
  /** Gradacja projektu (`project.defaultPeriod`) — ta sama, z jaką otwiera się Overview. */
  defaultPeriodType?: VisibilityReportPeriodType;
  /**
   * Modele WŁAŚCICIELA projektu. Przekazane wprost, nie przez `EffectiveFeaturesProvider`: superadmin
   * ma wybierać z listy klienta, a nie ze swojej — a jawny prop nie zależy od tego, gdzie w drzewie
   * renderuje się okno.
   */
  features?: string[];
}

const VisibilityReportDialog: React.FC<VisibilityReportDialogProps> = ({
  visible,
  onHide,
  onGenerate,
  isBusy,
  error,
  projectId,
  brand,
  minDate,
  maxDate,
  defaultPeriodType,
  features,
}) => {
  const { t, i18n } = useTranslation();
  const { availableTypes } = useModelSelectOptions(features);
  const periodOptions = useMemo(() => standardPeriodOptions(t), [t]);

  const [selectedPeriod, setSelectedPeriod] = useState<StandardPeriodType>('LAST_30_DAYS');
  // Gradacja startuje na `project.defaultPeriod` — TYM, z którym otwiera się Overview (Panel buduje
  // z niego adres przeglądu). `getPeriod()` czyta tylko URL i poza projektem zawsze dawało DAILY.
  const [periodType, setPeriodType] = useState<VisibilityReportPeriodType>(
    () => (defaultPeriodType ?? getPeriod()) as VisibilityReportPeriodType
  );
  const [range, setRange] = useState<Date[]>([]);
  // `null` = użytkownik nie ruszył modeli, więc podpowiadamy je z danych projektu.
  const [pickedTypes, setPickedTypes] = useState<ChatType[] | null>(null);
  // Pusty wybór = wszystkie prompty projektu (tak samo czyta to backend). Trzymamy KLUCZE wierszy
  // (`g:<id>` / `p:<id>`), nie id promptów — grupa jest jednym wyborem, a rozwijamy ją dopiero przy
  // wysyłce, bo kontrakt niesie płaską listę promptów.
  const [pickedKeys, setPickedKeys] = useState<string[]>([]);

  const { monitorPrompts } = useGetMonitorsForProject(projectId);
  const { data: promptGroups } = usePromptGroups(projectId);

  // Prompty zebrane w grupę pokazujemy jako JEDEN wiersz z nazwą grupy — tak samo, jak liczy je
  // raport (patrz `buildPrompts`). Grupa staje w miejscu swojego pierwszego promptu, więc kolejność
  // listy nie skacze po założeniu grupy.
  const { promptOptions, promptIdsByKey } = useMemo(() => {
    const groupsById = new Map((promptGroups ?? []).map((group) => [group.id, group]));
    const rows: Array<{ key: string; label: string; isGroup: boolean }> = [];
    const byKey = new Map<string, number[]>();

    for (const prompt of monitorPrompts ?? []) {
      if (prompt.status === 'DELETED' || prompt.status === 'TO_DELETE') {
        continue;
      }

      const group = prompt.promptGroupId ? groupsById.get(prompt.promptGroupId) : undefined;
      const key = group ? `g:${group.id}` : `p:${prompt.id}`;
      const members = byKey.get(key);
      if (members) {
        members.push(prompt.id);
        continue;
      }

      byKey.set(key, [prompt.id]);
      rows.push({ key, label: group ? group.name : prompt.prompt, isGroup: !!group });
    }

    // Wiersze budujemy po przejściu całej listy, bo tag grupy niesie liczbę jej promptów.
    const options: CheckboxSelectOption<string>[] = rows.map((row) => ({
      value: row.key,
      label: row.label,
      content: row.isGroup ? (
        <span className="visibility-report-dialog__group-row">
          <span className="visibility-report-dialog__group-tag">
            <span className="ico--folder ico--xxs" aria-hidden="true" />
            {`${t('reportPromptsGroupTag')} ${byKey.get(row.key)?.length ?? 0}`}
          </span>
          {row.label}
        </span>
      ) : undefined,
    }));

    return { promptOptions: options, promptIdsByKey: byKey };
  }, [monitorPrompts, promptGroups, t]);

  // Pusty stan = komplet; do samej kontrolki podajemy już rozwiniętą listę, żeby wiersz „wszystkie"
  // był zaznaczony od startu.
  const selectedKeys = pickedKeys.length
    ? pickedKeys
    : promptOptions.map((option) => option.value);
  // Licznik mówi o PROMPTACH, nie o wierszach — tyle ich wejdzie do raportu i tyle poda okładka.
  const selectedPromptIds = selectedKeys.flatMap((key) => promptIdsByKey.get(key) ?? []);
  const promptsCount = selectedPromptIds.length;

  // Zakres startowy liczy ten sam helper, co filtry panelu: 30 dni, ale nie wcześniej niż pierwszy
  // dzień z danymi. Bez tego raport startowałby na oknie, w którym projekt jeszcze nie istniał.
  // Okno jest montowane na czas otwarcia, więc to jednocześnie reset przy każdym wejściu.
  useEffect(() => {
    const defaults = calculateDefaultDateRange(minDate ?? new Date());
    setSelectedPeriod(defaults.period);
    setRange(defaults.range);
    setPickedTypes(null);
    setPickedKeys([]);
    setPeriodType((defaultPeriodType ?? getPeriod()) as VisibilityReportPeriodType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate, defaultPeriodType]);

  const days = range[0] && range[1] ? differenceInCalendarDays(range[1], range[0]) + 1 : 0;

  // Modele, które w TYM zakresie cokolwiek zebrały — to samo źródło, z którego panel bierze realny
  // czas życia modelu w projekcie.
  const { modelDataDateRanges, isLoading: isModelsLoading } = useGetProjectModelDataDateRanges(
    projectId,
    range[0] ?? null,
    range[1] ?? null,
    visible
  );
  const typesWithData = useMemo(
    () => chatTypesWithData(modelDataDateRanges.map((entry) => entry.model), availableTypes),
    [modelDataDateRanges, availableTypes]
  );

  // Domyślnie zaznaczone są modele, które w tym zakresie coś zebrały. Gdy zapytanie już wróciło i nie
  // ma ani jednego takiego modelu, zaznaczamy komplet dostępnych — inaczej okno startuje z pustym
  // wyborem i przycisk „Generuj" jest martwy, mimo że użytkownik niczego nie odznaczył.
  const selectedTypes =
    pickedTypes ?? (typesWithData.length > 0 || isModelsLoading ? typesWithData : availableTypes);

  const periodTypeOptions = useMemo(
    () => [
      { label: t('dailyFilter'), value: 'DAILY' as const },
      { label: t('weeklyFilter'), value: 'WEEKLY' as const },
      { label: t('monthlyFilter'), value: 'MONTHLY' as const },
    ],
    [t]
  );

  const periodError = days > MAX_DAYS ? t('visibilityReport.periodTooLong') : null;
  const canSubmit = !isBusy && days > 0 && !periodError && selectedTypes.length > 0;

  // Podgląd niesie tę samą etykietę, co gotowy raport — inaczej okno obiecuje inny nagłówek,
  // niż użytkownik zobaczy po wygenerowaniu.
  const periodLabel =
    range[0] && range[1]
      ? formatPeriodLabel(
          format(range[0], 'yyyy-MM-dd'),
          format(range[1], 'yyyy-MM-dd'),
          periodLocaleFor(i18n.language)
        )
      : '';

  const handleGenerate = () => {
    if (!canSubmit) {
      return;
    }
    onGenerate(
      format(range[0], 'yyyy-MM-dd'),
      format(range[1], 'yyyy-MM-dd'),
      // Komplet modeli konta = brak zawężenia; kontrakt niesie wtedy pustą listę.
      chatTypesToReportModels(selectedTypes, availableTypes),
      // Tak samo z promptami: komplet oddajemy jako pustą listę. Porównujemy WIERSZE, nie prompty —
      // przy grupach jest ich mniej niż promptów, a komplet to zaznaczone wszystkie wiersze.
      selectedKeys.length === promptOptions.length ? [] : selectedPromptIds,
      periodType
    );
  };

  return (
    <Dialog
      className="dialog-box visibility-report-dialog"
      header={t('visibilityReport.dialogTitle')}
      visible={visible}
      onHide={onHide}
      draggable={false}
      dismissableMask
      footer={
        <div className="visibility-report-dialog__footer">
          {(periodError || error) && <span className="form__error">{periodError || error}</span>}
          <button type="button" className="btn btn--sm" disabled={!canSubmit} onClick={handleGenerate}>
            <span className={`${isBusy ? 'ico--loader' : 'ico--report'} ico--xs`} aria-hidden="true" />
            {isBusy ? t('visibilityReport.generating') : t('visibilityReport.generateBtn')}
          </button>
        </div>
      }
    >
      <div className="visibility-report-dialog__body">
        <div className="visibility-report-dialog__settings">
          <label className="visibility-report-dialog__label">
            {t('visibilityReport.periodLabel')}
            <CustomTooltip
              tooltipText="visibilityReport.periodHint"
              addMargin={false}
              iconSizeClass="ico--xxs"
            />
          </label>
          {/* Ta sama oprawa co na pasku filtrów projektu — `FilterBox` bez zakładek okresu rysuje
              ramkę wokół pickera; bez niego kontrolka wygląda inaczej niż wszędzie indziej. */}
          <FilterBox
            items={[]}
            children={
              <DateRangePicker
                value={range}
                onChange={setRange}
                selectedPeriod={selectedPeriod}
                onPeriodChange={(period) => setSelectedPeriod(period as StandardPeriodType)}
                minDate={minDate}
                maxDate={maxDate}
                periodOptions={periodOptions}
                calculateRange={calculateStandardRange}
                defaultPeriod="LAST_30_DAYS"
                oldestDate={minDate}
              />
            }
          />

          <label className="visibility-report-dialog__label">
            {t('visibilityReport.periodTypeLabel')}
            <CustomTooltip
              tooltipText="visibilityReport.periodTypeHint"
              addMargin={false}
              iconSizeClass="ico--xxs"
            />
          </label>
          <Dropdown
            value={periodType}
            options={periodTypeOptions}
            onChange={(e) => setPeriodType(e.value as VisibilityReportPeriodType)}
            disabled={isBusy}
            className="visibility-report-dialog__period-type"
          />

          <label className="visibility-report-dialog__label">
            {t('visibilityReport.promptsLabel')}
            <CustomTooltip
              tooltipText="visibilityReport.promptsHint"
              addMargin={false}
              iconSizeClass="ico--xxs"
            />
          </label>
          <CheckboxSelect
            selected={selectedKeys}
            onChange={setPickedKeys}
            options={promptOptions}
            allLabel={t('visibilityReport.promptsAll')}
            triggerContent={
              <span className="checkbox-select__label">
                {selectedKeys.length === promptOptions.length
                  ? t('visibilityReport.promptsAll')
                  : t('visibilityReport.promptsSelected', { count: promptsCount })}
              </span>
            }
            searchPlaceholder={t('visibilityReport.promptsSearch')}
            maxListHeight={260}
            className="visibility-report-dialog__prompts"
          />

          <label className="visibility-report-dialog__label">
            {t('visibilityReport.modelsLabel')}
            <CustomTooltip
              tooltipText="visibilityReport.modelsHint"
              addMargin={false}
              iconSizeClass="ico--xxs"
            />
          </label>
          <ModelSelect selected={selectedTypes} onChange={setPickedTypes} features={features} />
          {!isModelsLoading && days > 0 && typesWithData.length === 0 && (
            <span className="visibility-report-dialog__warning">
              {t('visibilityReport.noDataInPeriod')}
            </span>
          )}
        </div>

        <div className="visibility-report-dialog__preview">
          <span className="visibility-report-dialog__preview-label">
            {t('visibilityReport.previewLabel')}
          </span>
          <VisibilityReportPreview
            brand={brand}
            periodLabel={periodLabel}
            models={selectedTypes}
            promptsCount={promptsCount}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default VisibilityReportDialog;
